export const landmarkCategoryLabels: Record<string, string> = {
  main_road: "ຖະໜົນຫຼັກ",
  market: "ຕະຫຼາດ",
  hospital: "ໂຮງໝໍ",
  school: "ໂຮງຮຽນ",
  bank: "ທະນາຄານ",
  government: "ສຳນັກງານລັດ",
  transport: "ຂົນສົ່ງ",
  other: "ອື່ນໆ",
};

export type LandmarkLabelVariant = "full" | "compact" | "hidden";

export type LandmarkLabelCandidate = {
  id: number;
  x: number;
  y: number;
  meters: number;
  isFocused: boolean;
};

export type LandmarkLabelPlacement = {
  id: number;
  variant: LandmarkLabelVariant;
  side: "left" | "right";
  width: number;
  height: number;
};

type LabelLayoutOptions = {
  isMobile: boolean;
  mapWidth: number;
  reservedRight?: number;
};

const LAO_FALLBACK_LABEL = "ຈຸດສຳຄັນ";
// Leaflet divIcons render outside the React/Tailwind tree, so preserve the
// app's multilingual type system explicitly in their self-contained markup.
export const LANDMARK_APP_FONT_STACK = "Inter,Phetsarath,NotoSansLao,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif";

export function formatLandmarkCategory(value: unknown): string {
  return landmarkCategoryLabels[String(value ?? "")] ?? LAO_FALLBACK_LABEL;
}

export function normalizeLandmarkText(value: unknown, fallback = LAO_FALLBACK_LABEL): string {
  const normalized = String(value ?? "")
    .replace(/\s+/g, " ")
    .trim();
  return normalized || fallback;
}

export function truncateLandmarkText(value: unknown, maxLength: number): string {
  const text = normalizeLandmarkText(value);
  return text.length <= maxLength ? text : `${text.slice(0, Math.max(1, maxLength - 1)).trimEnd()}…`;
}

export function escapeLandmarkHtml(value: unknown): string {
  return normalizeLandmarkText(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

export function buildLandmarkTooltipText(item: { name?: unknown; category?: unknown; note?: unknown }): string {
  const name = normalizeLandmarkText(item.name);
  const category = formatLandmarkCategory(item.category);
  const note = String(item.note ?? "").replace(/\s+/g, " ").trim();
  return note ? `${name} · ${category} · ${note}` : `${name} · ${category}`;
}

export function buildLandmarkLabelHtml(
  item: { name?: unknown; category?: unknown; note?: unknown },
  variant: Exclude<LandmarkLabelVariant, "hidden">,
  isFocused: boolean,
): string {
  const title = escapeLandmarkHtml(truncateLandmarkText(item.name, variant === "full" ? 38 : 20));
  const category = escapeLandmarkHtml(formatLandmarkCategory(item.category));
  const note = String(item.note ?? "").replace(/\s+/g, " ").trim();
  const detail = variant === "full" && note
    ? `${category} · ${escapeLandmarkHtml(truncateLandmarkText(note, 40))}`
    : category;
  const compactStyle = variant === "compact" ? "max-width:132px;min-height:26px" : "width:196px;min-height:42px";
  const focusStyle = isFocused ? "border-color:#002975;box-shadow:0 0 0 3px rgba(0,41,117,.18),0 2px 8px rgba(15,23,42,.26)" : "border-color:rgba(15,157,88,.55);box-shadow:0 2px 7px rgba(15,23,42,.22)";
  const detailStyle = variant === "compact" ? "display:none" : "display:block";
  return `<div data-landmark-location-label="true" aria-hidden="true" style="${compactStyle};pointer-events:none;box-sizing:border-box;overflow:hidden;border:1px solid;border-radius:10px;background:rgba(255,255,255,.96);padding:4px 8px;color:#10223f;font-family:${LANDMARK_APP_FONT_STACK};font-size:12px;font-weight:600;line-height:1.25;letter-spacing:0;${focusStyle}"><span style="display:block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${title}</span><span style="${detailStyle};margin-top:2px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:#526077;font-family:${LANDMARK_APP_FONT_STACK};font-size:10px;font-weight:500;line-height:1.2;letter-spacing:0">${detail}</span></div>`;
}

function sizeForVariant(variant: Exclude<LandmarkLabelVariant, "hidden">): { width: number; height: number } {
  return variant === "full" ? { width: 196, height: 46 } : { width: 132, height: 28 };
}

function doesOverlap(
  a: { left: number; top: number; width: number; height: number },
  b: { left: number; top: number; width: number; height: number },
): boolean {
  return a.left < b.left + b.width && a.left + a.width > b.left && a.top < b.top + b.height && a.top + a.height > b.top;
}

/**
 * Produces a deterministic display-only layout. Focused evidence has priority;
 * all other labels fall back from full to compact to hidden before any marker,
 * distance badge, geometry, or saved record is touched.
 */
export function resolveLandmarkLabelLayout(
  candidates: LandmarkLabelCandidate[],
  { isMobile, mapWidth, reservedRight = 0 }: LabelLayoutOptions,
): Map<number, LandmarkLabelPlacement> {
  const occupied: Array<{ left: number; top: number; width: number; height: number }> = [];
  const result = new Map<number, LandmarkLabelPlacement>();
  const ordered = [...candidates].sort((a, b) => Number(b.isFocused) - Number(a.isFocused) || a.meters - b.meters || a.id - b.id);

  for (const candidate of ordered) {
    const preferred: Exclude<LandmarkLabelVariant, "hidden"> = candidate.isFocused || !isMobile ? "full" : "compact";
    const alternatives: Exclude<LandmarkLabelVariant, "hidden">[] = preferred === "full" ? ["full", "compact"] : ["compact"];
    let resolved: LandmarkLabelPlacement | null = null;

    for (const variant of alternatives) {
      const { width, height } = sizeForVariant(variant);
      const side = candidate.x + width + 16 <= mapWidth - reservedRight ? "right" : "left";
      const box = {
        left: side === "right" ? candidate.x + 12 : candidate.x - width - 12,
        top: candidate.y - height - 18,
        width,
        height,
      };
      if (!occupied.some((occupiedBox) => doesOverlap(box, occupiedBox))) {
        occupied.push(box);
        resolved = { id: candidate.id, variant, side, width, height };
        break;
      }
    }

    result.set(candidate.id, resolved ?? { id: candidate.id, variant: "hidden", side: "right", width: 0, height: 0 });
  }

  return result;
}
